import { scaleSequential } from 'd3-scale';

import { colors } from '../../../core/theme/tokens';
import type { AnalyticsDailyPoint } from '../../../types/analytics';

export type CalendarCell = {
  date: string;
  amount: number;
  transactionCount: number;
  /** Week column index within the year (Monday-start). */
  weekIndex: number;
  /** 0 = Monday … 6 = Sunday. */
  dayOfWeek: number;
  month: number;
};

export type CalendarLayout = {
  year: number;
  cells: CalendarCell[];
  weekCount: number;
  cellSize: number;
  gap: number;
  width: number;
  height: number;
  leftPad: number;
  topPad: number;
  maxAmount: number;
  colorFor: (amount: number) => string;
  monthLabels: Array<{ label: string; x: number }>;
};

const DAY_MS = 86_400_000;

function dayKey(date: Date): string {
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(
    date.getUTCDate(),
  ).padStart(2, '0')}`;
}

/** Previous Monday on/before `date` (UTC). */
function utcMonday(date: Date): Date {
  const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  const day = d.getUTCDay(); // 0 Sun … 6 Sat
  const daysSinceMonday = day === 0 ? 6 : day - 1;
  d.setUTCDate(d.getUTCDate() - daysSinceMonday);
  return d;
}

/** 0 = Monday … 6 = Sunday. */
function mondayDayIndex(date: Date): number {
  return (date.getUTCDay() + 6) % 7;
}

function lerpChannel(a: number, b: number, t: number): number {
  return Math.round(a + (b - a) * t);
}

function hexToRgb(hex: string): [number, number, number] {
  const raw = hex.replace('#', '');
  const n = Number.parseInt(raw, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function mixHex(a: string, b: string, t: number): string {
  const [ar, ag, ab] = hexToRgb(a);
  const [br, bg, bb] = hexToRgb(b);
  const r = lerpChannel(ar, br, t);
  const g = lerpChannel(ag, bg, t);
  const bl = lerpChannel(ab, bb, t);
  return `#${((1 << 24) | (r << 16) | (g << 8) | bl).toString(16).slice(1)}`;
}

/**
 * Empty → soft amber → deep amber → red as spend rises
 * (Observable-style intensity, mapped onto budget spend tokens).
 */
export function spendHeatColor(t: number): string {
  const x = Math.max(0, Math.min(1, t));
  if (x <= 0) return colors.border;
  if (x < 0.55) return mixHex(colors.surfaceElevated, colors.warning, 0.35 + x * 1.18);
  if (x < 0.85) return mixHex(colors.warning, '#D97706', (x - 0.55) / 0.3);
  return mixHex('#D97706', colors.danger, (x - 0.85) / 0.15);
}

/**
 * Build an Observable-style year calendar: columns = weeks, rows = weekdays.
 */
export function buildSpendCalendarLayout(options: {
  year: number;
  days: AnalyticsDailyPoint[];
  width: number;
  leftPad?: number;
  topPad?: number;
  gap?: number;
}): CalendarLayout {
  const leftPad = options.leftPad ?? 36;
  const topPad = options.topPad ?? 22;
  const gap = options.gap ?? 3;
  const year = options.year;

  const byDate = new Map(options.days.map((d) => [d.date, d]));
  const yearStart = new Date(Date.UTC(year, 0, 1));
  const yearEnd = new Date(Date.UTC(year, 11, 31));
  const firstMonday = utcMonday(yearStart);

  const cells: CalendarCell[] = [];
  let maxWeek = 0;
  for (
    let cursor = new Date(yearStart);
    cursor.getTime() <= yearEnd.getTime();
    cursor.setUTCDate(cursor.getUTCDate() + 1)
  ) {
    const date = dayKey(cursor);
    const hit = byDate.get(date);
    const weekIndex = Math.round(
      (utcMonday(cursor).getTime() - firstMonday.getTime()) / (7 * DAY_MS),
    );
    maxWeek = Math.max(maxWeek, weekIndex);
    cells.push({
      date,
      amount: hit?.amount ?? 0,
      transactionCount: hit?.transactionCount ?? 0,
      weekIndex,
      dayOfWeek: mondayDayIndex(cursor),
      month: cursor.getUTCMonth(),
    });
  }

  const weekCount = maxWeek + 1;
  const usable = Math.max(options.width - leftPad, 120);
  const cellSize = Math.max(
    9,
    Math.min(15, Math.floor((usable - gap * (weekCount - 1)) / weekCount)),
  );
  const width = leftPad + weekCount * cellSize + Math.max(0, weekCount - 1) * gap;
  const height = topPad + 7 * cellSize + 6 * gap;

  const maxAmount = Math.max(...cells.map((c) => c.amount), 0);
  const colorScale = scaleSequential(spendHeatColor).domain([0, Math.max(maxAmount, 1)]);

  const monthLabels: Array<{ label: string; x: number }> = [];
  for (let month = 0; month < 12; month++) {
    const first = new Date(Date.UTC(year, month, 1));
    const weekIndex = Math.round(
      (utcMonday(first).getTime() - firstMonday.getTime()) / (7 * DAY_MS),
    );
    monthLabels.push({
      label: first.toLocaleDateString(undefined, { month: 'short', timeZone: 'UTC' }),
      x: leftPad + weekIndex * (cellSize + gap),
    });
  }

  return {
    year,
    cells,
    weekCount,
    cellSize,
    gap,
    width,
    height,
    leftPad,
    topPad,
    maxAmount,
    colorFor: (amount: number) => (amount <= 0 ? colors.border : colorScale(amount)),
    monthLabels,
  };
}

export function cellRect(
  layout: CalendarLayout,
  cell: CalendarCell,
): { x: number; y: number; size: number } {
  return {
    x: layout.leftPad + cell.weekIndex * (layout.cellSize + layout.gap),
    y: layout.topPad + cell.dayOfWeek * (layout.cellSize + layout.gap),
    size: layout.cellSize,
  };
}

/** Row labels for Monday-start weeks (index matches `CalendarCell.dayOfWeek`). */
export const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;

/**
 * Sanity helper for tests / debugging: UTC calendar date → Monday-start row index.
 * 0=Mon … 6=Sun. Must stay in sync with WEEKDAY_LABELS.
 */
export function weekdayRowForUtcDate(isoDate: string): {
  dayOfWeek: number;
  label: (typeof WEEKDAY_LABELS)[number];
} {
  const d = new Date(`${isoDate}T12:00:00.000Z`);
  const dayOfWeek = mondayDayIndex(d);
  return { dayOfWeek, label: WEEKDAY_LABELS[dayOfWeek]! };
}
